import { rem, spacing, text, status, token } from '@thiememeulenhoff/lit-sdk';
import { BreakpointSmall } from '@thiememeulenhoff/tokens/core';
import { css, CSSResult, unsafeCSS } from 'lit';
import { tagName } from '../../../helpers/element.helper';
import { ToasterStyle, ToasterType, toasterTypes } from './toaster.types';

export const tag = tagName('toaster-message');

const styles: Array<CSSResult> = [];

styles.push(css`
  :host {
    --calculated-height: ${rem(100)};

    ${text('body', 'medium', 'regular')};

    border-inline-end-style: solid;
    border-inline-end-width: ${rem(2)};
    border-radius: ${spacing(100, 0, 0, 100)};
    color: ${token('text-primary')};
    display: flex;
    gap: ${spacing(75)};
    inline-size: auto;
    max-inline-size: ${rem(330)};
    overflow: hidden;
    padding: ${spacing(150)};
  }

  h3,
  p {
    margin: 0;
  }

  p:empty {
    display: none;
  }

  h3 {
    ${text('body', 'medium', 'bold')};
    flex-grow: 1;
    gap: ${spacing(75)};
  }

  a-icon-button::part(button) {
    min-block-size: ${rem(26)};
  }

  .header-wrapper {
    display: flex;
    gap: ${spacing(50)};
  }

  .icon-wrapper {
    align-items: center;
    block-size: ${rem(24)};
    display: flex;
  }

  .content-wrapper {
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    gap: ${spacing(100)};
  }

  .close-button {
    background-color: transparent;
    block-height: ${rem(24)};
    border: unset;
    color: ${token('text-secondary')};
    cursor: pointer;
    flex: 0 0 ${rem(24)};
    inline-size: ${rem(24)};
    padding: 0;
    padding-block-start: ${spacing(75)};
  }

  :host(:not([dismissible])) .close-button {
    display: none;
  }

  :host(.move-in) {
    animation: moveLeft 0.25s forwards;
  }

  :host(.dismissing) {
    animation: moveRight 0.25s forwards;
  }

  :host(.shrink) {
    animation: shrink 0.25s forwards;
  }

  @media (width <= ${unsafeCSS(BreakpointSmall)}) {
    :host {
      margin-inline: ${spacing(100)};
      max-inline-size: calc(100dvw - ${spacing(400)});
    }
  }

  @keyframes moveLeft {
    0% {
      transform: translateX(100%);
    }

    100% {
      transform: translateX(0);
    }
  }

  @keyframes moveRight {
    0% {
      opacity: 1;
      transform: translateX(0);
    }
    100% {
      opacity: 0;
      transform: translateX(120%);
    }
  }

  @keyframes shrink {
    0% {
      block-size: var(--calculated-height);
      opacity: 0;
      padding: ${spacing(150)};
      transform: translateX(120%);
    }
    100% {
      block-size: 0;
      margin-block-start: 0;
      opacity: 0;
      padding: 0;
      transform: translateX(120%);
    }
  }
`);

export const ToasterStyles: Record<ToasterType, ToasterStyle> = {
  success: {
    icon: { color: status('success', 'foreground'), name: 'success-solid' },
    background: status('success', 'background'),
    border: status('success', 'foreground'),
  },
  error: {
    icon: { color: status('danger', 'foreground'), name: 'error-solid' },
    background: status('danger', 'background'),
    border: status('danger', 'foreground'),
  },
  warning: {
    icon: { color: status('warning', 'foreground'), name: 'danger-solid' },
    background: status('warning', 'background'),
    border: status('warning', 'foreground'),
  },
  info: {
    icon: { color: status('info', 'foreground'), name: 'info-solid' },
    background: status('info', 'background'),
    border: status('info', 'foreground'),
  },
  neutral: {
    icon: { color: token('text-secondary'), name: 'user' },
    background: token('background-tertiary'),
    border: token('text-secondary'),
  },
};

const toasterMessageTypeStyle = (toasterType: ToasterType) => {
  const selector = unsafeCSS(`[type=${toasterType}]`);
  const iconClass = unsafeCSS(`.toaster-icon-${toasterType}`);
  const { background, border, icon } = ToasterStyles[toasterType];

  return css`
    :host(${selector}) {
      background-color: ${background};
      border-color: ${border};
    }

    ${iconClass} {
      color: ${icon.color};
    }
  `;
};

toasterTypes.forEach((type: ToasterType) =>
  styles.push(toasterMessageTypeStyle(type)),
);

export const toasterMessageStyles = styles;
